import { FormControlLabel, Radio, RadioGroup, TextField } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import Color from 'color';
import {
  profile_boy,
  profile_female,
  profile_girl,
  profile_male,
  profile_none,
} from 'components/base/images';
import MuiButton from 'components/common/button/MuiButton';
import ImgCrop from 'components/common/images/ImgCrop';
import CustomDatePickerV2 from 'components/common/input/CustomDatePickerV2';
import MuiWrapper from 'components/common/input/MuiWrapper';
import AlertModalContent from 'components/common/modal/contents/AlertModalContent';
import { StyledPlainButtonOuter } from 'components/common/styled/Button';
import T from 'components/common/text/T';
import useDateInput from 'lib/hooks/useDateInput';
import useFileInput from 'lib/hooks/useFileInput';
import useInput from 'lib/hooks/useInput';
import { calculateAge, checkSpace, regChartNumber, validInputString } from 'lib/library';
import { IPatientProfileImage } from 'lib/types/patientTypes';
import { useDidUpdateEffect } from 'lib/utils';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppActions, PatientActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import styled, { createGlobalStyle } from 'styled-components';
import { color } from 'styles/utils';
interface PatientModalProps {
  type: string;
  ptCode?: string;
  name?: string;
  onCloseModal: () => void;
}

export interface PatientInfoFormInput {
  chartNumber: string;
  firstName: string;
  lastName: string;
  birthday: Date;
  gender: 'M' | 'F' | 'N';
  comment: string;
  identification: string;
  newIdentification: string;
  profileImgIdx: string;
  profileImg: string;
  patientCodeIdx: number;
  patientCode: string;
  patientIdx?: number;
  pms?: string;
}

/**
 * @param {PatientModalProps} { selectedPatient }
 * @return {*}
 */

function PatientModalContainer({ type, ptCode, name, onCloseModal }: PatientModalProps) {
  const {
    language,
    newPatient: { patientInfo: newPatient, msg },
    newPatientSuccess,
    newPatientFailure,
    fetchPatient: { patientInfo },
    fetchPatientSuccess,
    editPatientSuccess,
    editPatientFailure,
    checkChartNumber: { msg: checkChartNumMsg },
    checkChartNumberSuccess,
  }: {
    language: string;
    newPatient: {
      patientInfo: PatientInfoFormInput;
      msg: string;
    };
    newPatientSuccess: boolean;
    newPatientFailure: boolean;
    fetchPatient: {
      patientInfo: PatientInfoFormInput;
    };
    fetchPatientSuccess: boolean;
    editPatientSuccess: boolean;
    editPatientFailure: boolean;
    checkChartNumber: {
      msg: string;
    };
    checkChartNumberSuccess: boolean;
  } = useShallowAppSelector(state => ({
    language: state.base.language,
    newPatient: state.patient.newPatient.data || { patientInfo: {}, msg: {} },
    newPatientSuccess: state.patient.newPatient.success,
    newPatientFailure: state.patient.newPatient.failure,
    fetchPatient: state.patient.patient.data || { patientInfo: {} },
    fetchPatientSuccess: state.patient.patient.success,
    editPatientSuccess: state.patient.editPatient.success,
    editPatientFailure: state.patient.editPatient.failure,
    checkChartNumber: state.patient.checkChartNumber.data || { msg: {} },
    checkChartNumberSuccess: state.patient.checkChartNumber.success,
  }));

  const { t } = useTranslation();
  const { ipcRenderer } = window;
  const [tooltipText, setTooltipText] = useState<string>(t('PATIENT_SORTATION_TOOLTIP'));

  const [isImageChange, setIsImageChange] = useState<boolean>(false);
  const profileImage = useFileInput({
    file: null,
    name: '',
  });
  const profileIdx = useInput(null);
  const deleteProfileImageIdx = useInput(null);
  const profileFileRef = useRef<null | any>(null);
  const [profileFilePreview, setProfileFilePreview] = useState<object | any>(null);

  const profileImageData: IPatientProfileImage = {
    profileImage,
    profileIdx,
    deleteProfileImageIdx,
  };

  const profileImageIdx = null;
  const [chartNumberError, setChartNumberError] = useState<boolean>(false);
  const [chartNumberErrPending, setChartNumberErrPending] = useState<boolean>(false);
  const chartNumber = useInput('');
  const firstName = useInput('');
  const lastName = useInput('');
  const birthday = useDateInput(null);
  const gender = useInput('M');
  const comment = useInput('');
  const identification = useInput('');
  const [isPicker, setIsPicker] = useState<boolean>(false);

  const [buttonValid, setButtonValid] = useState<boolean>(true);
  const maxMemo = 300;
  const [memoLength, setMemoLength] = useState<number>(0);

  const genderList = [
    {
      key: 'M',
      title: t('GLOBAL_MALE'),
    },
    {
      key: 'F',
      title: t('GLOBAL_FEMALE'),
    },
    {
      key: 'N',
      title: t('GLOBAL_NONE'),
    },
  ];

  const profileNotification = {
    title: t('GLOBAL_ALERT'),
    mainText: t('NOT_USE_PRORILE_IMAGE'),
    subText: t('NOT_USE_PRORILE_IMAGE_DETAIL'),
  };

  const alertNotificationPopup = () => {
    AppActions.add_popup({
      isOpen: true,
      isCloseIcon: true,
      width: 570,
      title: '',
      content: (
        <AlertModalContent
          title={profileNotification.title}
          mainText={profileNotification.mainText}
          subText={profileNotification.subText}
        />
      ),
      onClick() {
        return;
      },
      onCancel() {
        return;
      },
    });
  };

  const alertDuplicateIdentification = (identification: string) => {
    AppActions.add_popup({
      isOpen: true,
      isCloseIcon: true,
      width: 570,
      title: '',
      content: (
        <AlertModalContent
          title={t('PATIENT_DUPLICATE_IDENTIFICATION_TITLE', { identification })}
          mainText={t('PATIENT_DUPLICATE_IDENTIFICATION_CONTENTS', { identification })}
        />
      ),
      onClick() {
        onCloseModal();
        return;
      },
      onCancel() {
        onCloseModal();
        return;
      },
    });
  };

  const alertDuplicateChartNumber = (type: string) => {
    AppActions.add_popup({
      isOpen: true,
      isCloseIcon: true,
      width: 570,
      title: '',
      content: (
        <AlertModalContent
          title={t('PATIENT_DUPLICATE_CHARTNUMBER_TITLE')}
          mainText={t('PATIENT_DUPLICATE_CHARTNUMBER_CONTENTS')}
        />
      ),
      onClick() {
        // handleSubmitDuplicatePatientData(type);
        return;
      },
      onCancel() {
        // handleSubmitDuplicatePatientData(type);
        return;
      },
    });
  };

  const handleChangeProfileImage = (event: any) => {
    let reader = new FileReader();
    let file = event.target.files[0];

    if (!!file) {
      if (
        file.size > 10485760 ||
        file.type.split('/')[0] !== 'image' ||
        file.name.split('.')[0].length > 124
      ) {
        alertNotificationPopup();
      } else {
        reader.onloadend = () => {
          setProfileFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
        profileImage.onChange(event);
        setIsImageChange(true);
      }
    } else {
      setIsImageChange(true);
      return;
    }
  };

  const handleMakeChartNumber = () => {
    const date = moment().format('YYYYMMDDhhmmss');
    chartNumber.setValue(date);
  };

  const handleClickPicker = () => {
    setIsPicker(true);
  };

  const handleBlurPicker = () => {
    setIsPicker(false);
  };

  const handleSubmitDuplicatePatientData = (type: string) => {
    const submitData = {
      chartNumber: moment().format('YYYYMMDDhhmmss'),
      firstName: firstName.value,
      lastName: lastName.value,
      birthday: moment(birthday.value).format('YYYY-MM-DD'),
      gender: gender.value,
      comment: comment.value,
      identification: identification.value,
    };
    if (type === 'new') {
      PatientActions.new_patient_request(submitData);
    } else {
      PatientActions.edit_patient_request({ patientCode: ptCode, data: submitData });
    }
  };

  const handleSubmitPatientData = (type: string) => {
    const submitData = {
      chartNumber: chartNumber.value,
      firstName: firstName.value.trim(),
      lastName: lastName.value.trim(),
      birthday: !!birthday.value ? moment(birthday.value).format('YYYY-MM-DD') : '',
      gender: gender.value,
      comment: comment.value.trim(),
      identification: identification.value,
    };

    if (type === 'new') {
      PatientActions.new_patient_request(submitData);
    } else {
      PatientActions.edit_patient_request({ patientCode: ptCode, data: submitData });
    }
  };

  useDidUpdateEffect(() => {
    if (!!profileImageIdx) {
      // TODO: set profile img
      profileImage.setValue(profileImageIdx);
    } else if (!profileImageIdx && type == 'new') {
      profileImage.setValue(profile_male);
    } else {
      // TODO: 프로필 이미지 없고, 수정인 경우 => 프로필 이미지 성별,나이 분기
    }
  }, []);

  // init
  useEffect(() => {
    if (type === 'new') {
      handleMakeChartNumber();
      firstName.setValue(!!name ? name : '');
      profileImage.setValue(profile_male);
    } else {
      if (!ptCode) return;
      PatientActions.fetch_patient_request(ptCode);
    }
  }, [ptCode, type]);

  useEffect(() => {
    return () => {
      chartNumber.setValue('');
      birthday.setValue(null);
      firstName.setValue('');
      lastName.setValue('');
      identification.setValue('');
      comment.setValue('');
      gender.setValue('');
      if (type !== 'edit') {
        PatientActions.fetch_patient_clear();
      }
    };
  }, []);

  useDidUpdateEffect(() => {
    if (type === 'new') {
      setButtonValid(!firstName.value || !chartNumber.value || !!chartNumberError);
    } else {
      const firstNameLengthValid = firstName.value.length > 0;

      const isChangedChartNumber = chartNumber.value !== patientInfo.chartNumber;
      const isEmptyBirth = !birthday.value && !patientInfo.birthday;
      const isChangedBirth = !!isEmptyBirth
        ? false
        : !moment(birthday.value).isSame(patientInfo.birthday);
      const isChangedFirstName = firstName.value !== patientInfo.firstName;
      const isChangedLastName = lastName.value !== patientInfo.lastName;
      const isChangedGender = gender.value !== patientInfo.gender;
      const isChangedComment = comment.value !== patientInfo.comment;
      const isChangedIdentification = identification.value !== patientInfo.identification;

      const changedValidArray = [
        isChangedChartNumber,
        isChangedFirstName,
        isChangedBirth,
        isChangedLastName,
        isChangedGender,
        isChangedComment,
        isChangedIdentification,
      ];

      const changedValidData = changedValidArray.some(item => item === true);

      const validArray = [
        firstNameLengthValid,
        changedValidData,
        !chartNumberError,
        !chartNumberErrPending,
      ];

      const validData = validArray.some(item => item === false);

      setButtonValid(validData);
    }
  }, [
    type,
    chartNumber.value,
    firstName.value,
    lastName.value,
    birthday.value,
    gender.value,
    comment.value,
    identification.value,
    chartNumberError,
    chartNumberErrPending,
  ]);

  useEffect(() => {
    // 생년월일 바뀔때 팝업 컨트롤
    if (!birthday.value) return;
    handleBlurPicker();
  }, [birthday.value]);

  useDidUpdateEffect(() => {
    setProfileFilePreview(!!profileImage.value ? profileImage.value : null);
  }, [profileImage.value]);

  useDidUpdateEffect(() => {
    if (!fetchPatientSuccess) return;
    chartNumber.setValue(patientInfo.chartNumber);
    birthday.setValue(
      !!moment(patientInfo.birthday).isValid() ? moment(patientInfo.birthday) : null,
    );
    firstName.setValue(patientInfo.firstName);
    lastName.setValue(!!patientInfo.lastName ? patientInfo.lastName : '');
    identification.setValue(!!patientInfo.identification ? patientInfo.identification : '');
    comment.setValue(!!patientInfo.comment ? patientInfo.comment : '');
    gender.setValue(patientInfo.gender);
    if (!patientInfo.profileImgIdx) {
      // NOTE: 프로필 이미지 없는경우 디폴트 이미지
      setDefaultPatientImage(patientInfo.birthday, patientInfo.gender);
    } else {
      // TODO: 프로필 이미지있는 경우, 프로필이미지 다운로드 추가 예정
      // setProfileFilePreview()
    }
  }, [fetchPatientSuccess]);

  useDidUpdateEffect(() => {
    setDefaultPatientImage(birthday.value, gender.value);
  }, [birthday.value, gender.value]);

  const setDefaultPatientImage = (birthday: Date, gender: 'M' | 'F' | 'N') => {
    const patientAge = calculateAge(birthday);
    if (patientAge.isAdult) {
      if (gender === 'F') {
        setProfileFilePreview(profile_female);
      } else if (gender === 'M') {
        setProfileFilePreview(profile_male);
      } else {
        setProfileFilePreview(profile_none);
      }
    } else {
      if (gender === 'F') {
        setProfileFilePreview(profile_girl);
      } else if (gender === 'M') {
        setProfileFilePreview(profile_boy);
      } else {
        setProfileFilePreview(profile_none);
      }
    }
  };

  const handleChangeChartNumber = (chartNum: string) => {
    if (chartNum.length > 14) return;
    chartNumber.onChange({ value: chartNum });
    if (chartNum.length === 14) {
      if (type !== 'new' && patientInfo.chartNumber === chartNum) {
        setChartNumberError(false);
      } else {
        setChartNumberErrPending(true);
        PatientActions.check_chart_number_request({ chartNumber: chartNum });
      }
    }

    if (chartNum.length < 14) {
      setChartNumberError(false);
    }
  };

  useDidUpdateEffect(() => {
    if (!checkChartNumberSuccess) return;
    setChartNumberErrPending(false);
    if (checkChartNumMsg === 'DUPLICATE_CHART_NUMBER') {
      setChartNumberError(true);
    } else {
      setChartNumberError(false);
    }
  }, [checkChartNumberSuccess]);

  useDidUpdateEffect(() => {
    if (!newPatientSuccess) return;
    if (msg === 'DUPLICATE_PATIENT_NAME') {
      // NOTE: 생성 시, identification이 변경된 경우
      alertDuplicateIdentification(newPatient.newIdentification);
    } else {
      onCloseModal();
    }
  }, [newPatientSuccess]);

  useDidUpdateEffect(() => {
    if (!newPatientFailure) return;
    alertDuplicateChartNumber(type);
  }, [newPatientFailure]);

  useDidUpdateEffect(() => {
    if (!editPatientSuccess) return;
    onCloseModal();
  }, [editPatientSuccess]);

  useDidUpdateEffect(() => {
    if (!editPatientFailure) return;
    alertDuplicateChartNumber(type);
  }, [editPatientFailure]);

  return (
    <StyledPatientModal data-component-name="PatientModal">
      <div className="patientModal__wrapper">
        <div className="patientModal__container">
          <div className="patientModal__row profile">
            <div className="profile__image_box">
              <label>
                <ImgCrop width={166} height={200} src={profileFilePreview} />

                {/*  TODO: 추후 프로필파일 업로드 시 사용
                        <div className="partner_profile_image_edit_background">
                          <input
                            type="file"
                            accept=".gif,.png,.jpeg,.jpg"
                            id="partnerImage"
                            name="partnerImage"
                            hidden
                            ref={profileFileRef}
                            onChange={e => {
                              handleChangeProfileImage(e);
                            }}
                          />
                        </div>

                        <span className="partner_profile_image_edit_text">
                          <T>GLOBAL_UPLOAD</T>
                        </span> */}
              </label>
            </div>
            <div className="profile__info_box">
              <div className="profile__info_row chartNumber">
                <div className="profile__info_row_label">
                  <T>PATIENT_CHARTNUM</T>
                  <span style={{ color: 'red' }}>{` *`}</span>
                </div>
                <div className="profile__info_row_contents">
                  {!!patientInfo.pms ? (
                    <span>{chartNumber.value}</span>
                  ) : (
                    <MuiWrapper className="row_contents_wrapper chartNumber">
                      <TextField
                        minRows={1}
                        maxRows={1}
                        className="sm radius-md contents__input_box"
                        variant="outlined"
                        fullWidth
                        placeholder={t('PLACEHOLDER_CHARTNUMBER')}
                        value={chartNumber.value}
                        onChange={e => {
                          if (!!regChartNumber(e.target.value)) {
                            handleChangeChartNumber(e.target.value);
                          }
                          // if (e.target.value.length > 14) return;
                          // chartNumber.onChange(e);
                        }}
                        autoFocus
                      />
                    </MuiWrapper>
                  )}
                  {chartNumberError && (
                    <span className="chartNumber_error">
                      <T>ERROR_DUPLICATE_CHART_NUMBER</T>
                    </span>
                  )}
                  {/* <MuiButton
                    config={{
                      bgColor: '#fff',
                      borderColor: '#e1e1e1',
                      width: '36px',
                      height: '28px',
                    }}
                    className="xs chart_number_refresh"
                    variant="outlined"
                    onClick={handleMakeChartNumber}
                  >
                    <RefreshIcon htmlColor={color.blue} fontSize="inherit" />
                  </MuiButton> */}
                </div>
              </div>

              <div className="profile__info_row lastName">
                <div className="profile__info_row_label">
                  <T>GLOBAL_FIRST_NAME</T>
                  <span style={{ color: 'red' }}>{` *`}</span>
                </div>
                <div className="profile__info_row_contents">
                  <MuiWrapper className="row_contents_wrapper">
                    <TextField
                      minRows={1}
                      maxRows={1}
                      className="sm radius-md contents__input_box"
                      variant="outlined"
                      fullWidth
                      placeholder={t('GLOBAL_FIRST_NAME')}
                      value={firstName.value}
                      onChange={e => {
                        if (e.target.value.length > 14) return;
                        firstName.onChange({ value: e.target.value });
                      }}
                      // autoFocus
                    />
                  </MuiWrapper>
                  <StyledGlobalTooltip />
                  <Tooltip
                    className="identification__tooltip"
                    title={tooltipText}
                    placement="bottom-start"
                    disableHoverListener={false}
                    disableFocusListener={true}
                    disableTouchListener={true}
                  >
                    <div className="identification">
                      <MuiWrapper className="row_contents_wrapper identification">
                        <TextField
                          minRows={1}
                          maxRows={1}
                          className="sm radius-md contents__input_box"
                          variant="outlined"
                          fullWidth
                          // disabled={true}
                          value={identification.value}
                          // onChange={identification.onChange}
                          onChange={e => {
                            const val = e.target.value;
                            if (checkSpace(val)) return;
                            identification.onChange({ value: val.toUpperCase().trim() });
                          }}
                        />
                      </MuiWrapper>
                    </div>
                  </Tooltip>
                </div>
              </div>
              <div className="profile__info_row firstName">
                <div className="profile__info_row_label">
                  <T>GLOBAL_LAST_NAME</T>
                </div>
                <div className="profile__info_row_contents">
                  <MuiWrapper className="row_contents_wrapper">
                    <TextField
                      minRows={1}
                      maxRows={1}
                      className="sm radius-md contents__input_box"
                      variant="outlined"
                      fullWidth
                      placeholder={t('GLOBAL_LAST_NAME')}
                      value={lastName.value}
                      onChange={e => {
                        if (e.target.value.length > 14) return;
                        lastName.onChange({ value: e.target.value });
                      }}
                    />
                  </MuiWrapper>
                </div>
              </div>

              <div className="profile__info_row birthday">
                <div className="profile__info_row_label">
                  <T>GLOBAL_BIRTH</T>
                </div>
                <div className="profile__info_row_contents">
                  <MuiWrapper className="row_contents_wrapper">
                    <CustomDatePickerV2
                      type="date"
                      width={242}
                      borderColor={color.gray_week}
                      height={32}
                      // disabledDate
                      value={birthday.value}
                      onChange={birthday.onChange}
                      open={isPicker}
                      onFocus={() => {
                        handleClickPicker();
                      }}
                      onClick={() => {
                        handleClickPicker();
                      }}
                      onBlur={() => {
                        handleBlurPicker();
                      }}
                    />
                  </MuiWrapper>
                </div>
              </div>

              <div className="profile__info_row gender">
                <div className="profile__info_row_label">
                  <T>GLOBAL_GENDER</T>
                </div>
                <div className="profile__info_row_contents">
                  <MuiWrapper className="row_contents_wrapper gender">
                    <RadioGroup
                      className="gender_list_group"
                      aria-label="refund"
                      name="gender"
                      value={gender.value}
                      onChange={gender.onChange}
                    >
                      {genderList?.length > 0 &&
                        genderList.map((item, key) => {
                          return (
                            <FormControlLabel
                              key={item.key}
                              value={String(item.key)}
                              control={<Radio color="primary" size="small" />}
                              className="gender_radio_box"
                              label={<div className="gender_label">{item.title}</div>}
                            />
                          );
                        })}
                    </RadioGroup>
                  </MuiWrapper>
                </div>
              </div>
            </div>
          </div>
          <div className="patientModal__row comment">
            <div className="comment__label">
              <T>PATIENT_MEMO</T>
            </div>
            <div className="comment__input_box">
              <MuiWrapper config={{ height: 'auto' }}>
                <TextField
                  multiline
                  minRows={10}
                  maxRows={10}
                  variant="outlined"
                  fullWidth
                  value={comment.value}
                  onChange={e => {
                    if (e.target.value.length > maxMemo) return;
                    const checkText = validInputString(e.target.value);
                    // comment.onChange(e);
                    comment.onChange({ value: checkText });
                    setMemoLength(e.target.value.length);
                  }}
                />
              </MuiWrapper>
              <div className="comment__input_length">{`(${memoLength} / ${maxMemo})`}</div>
            </div>
          </div>
        </div>

        <StyledPlainButtonOuter
          className="partnerModalInput__form_button_box detail"
          backgroundColor={color.navy_blue}
          left="50%"
          width={370}
          height={76}
        >
          <MuiButton
            config={{
              width: '320px',
            }}
            disabled={buttonValid}
            disableElevation
            color="primary"
            variant="contained"
            disablebackground={color.navy_blue}
            disablefontcolor="#ffffff"
            className="xl border-radius-round partner_ok_button "
            onClick={() => {
              handleSubmitPatientData(type);
            }}
          >
            {type === 'new' ? <T>GLOBAL_SAVE</T> : <T>GLOBAL_MODIFY</T>}
          </MuiButton>
        </StyledPlainButtonOuter>
      </div>
    </StyledPatientModal>
  );
}

const StyledGlobalTooltip = createGlobalStyle<{}>`

.MuiTooltip-popper {
  .MuiTooltip-tooltipPlacementBottom {
    font-size: 12px;
    margin-top: 5px;
    margin-left: 50px;
    max-width: 450px;
  }
}
`;

const StyledPatientModal = styled.section<{}>`
  .patientModal__wrapper {
    position: relative;
    width: 100%;
    margin-bottom: 30px;
    background-color: #ffffff;
    border-radius: 15px;
    height: 542px;
    padding-top: 10px;
    padding: 30px 30px 50px 30px;
    .patientModal__container {
      .profile {
        display: flex;
        column-gap: 30px;
        margin-bottom: 30px;
        .profile__image_box {
          width: 166px;
          height: 200px;
          background-color: lightgray;
          border-radius: 3px;
          border: solid 1px #cacaca;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .profile__info_box {
          width: 728px;
          height: 200px;
          display: flex;
          flex-direction: column;
          row-gap: 10px;
          .profile__info_row {
            height: 32px;
            width: 100%;
            display: flex;
            align-items: center;
            .profile__info_row_label {
              width: 120px;
            }
            .profile__info_row_contents {
              display: flex;
              align-items: center;
              /* column-gap: 10px; */
              .chartNumber_error {
                margin-left: 10px;
                color: red;
              }
              .row_contents_wrapper {
                height: 32px;
                width: 242px;
                display: flex;
                align-items: center;
                /* &.chartNumber {
                  width: 206px;
                } */
                &.identification {
                  width: 70px;
                  margin-left: 10px;
                }
                &.gender {
                  width: 100%;
                }
                .gender_list_group {
                  .gender_radio_box {
                    margin-right: 30px;
                    display: flex;
                    align-items: center;
                    .gender_label {
                      margin-top: 4px;
                    }
                  }
                }
              }
              .contents__input_box {
                width: 100%;
                height: 32px;
              }
              .chart_number_refresh {
                font-size: 20px;
              }
            }
          }
        }
      }
      .comment {
        .comment__label {
          font-size: 19px;
          color: #444;
          height: 23px;
          margin-bottom: 10px;
        }
        .comment__input_box {
          width: 100%;
          height: 200px;
          position: relative;
          .comment__input_length {
            font-size: 12px;
            position: absolute;
            bottom: -20px;
            right: 0;
          }
        }
      }
    }
  }

  .partnerModalInput__form_button_box {
    .partner_ok_button {
      border: 1px solid #ffffff;
      position: relative;
      .partner_cancel_button {
        position: absolute;
        top: 50%;
        right: 10px;
        border: 1px solid #ffffff;
        border-radius: 18px;
        width: 36px;
        height: 36px;
        transform: translate(0, -50%);
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
    }

    .projectInformation__btn_box {
      position: relative;
      display: flex;
      .projectInformation__reject_btn {
        position: relative;
        z-index: 2;
      }
      .projectInformation__accept_btn {
        margin-left: -10px;
        background: rgb(0, 166, 226);
        background: linear-gradient(90deg, rgba(0, 166, 226, 1) 0%, rgba(8, 123, 238, 1) 100%);
        border: none;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        overflow: hidden;
        &:hover {
          background: ${`linear-gradient(
              90deg,
              ${Color('rgba(0, 166, 226, 1)').darken(0.12)} 0%,
              ${Color('rgba(8, 123, 238, 1)').darken(0.12)} 100%
            )`};
        }
        .btn-shadow-inset {
          position: absolute;
          top: -5px;
          left: -46px;
          width: 66px;
          height: 66px;
          border-radius: 33px;
          background-color: ${color.navy_blue};
          box-shadow: 3px 3px 6px rgba(0, 0, 0, 0.16);
        }
      }
    }
  }
`;

export default React.memo(PatientModalContainer);
